import { Suspense, lazy, useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate as RouterNavigate,
  useNavigate,
  useParams,
  useLocation,
  useNavigationType,
  type NavigateProps,
} from "react-router-dom";
import { AuthProvider, MODULE_VIEW_PERMISSIONS, useAuth } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/error/ErrorBoundary";
import { AuthGate } from "@/components/auth/AuthGate";
import { OfflineBanner } from "@/components/pwa/ConnectionStatusIndicator";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { debugLog, debugWarn } from "@/lib/debugLogger";
import MapPage from "./pages/MapPage";
import ClustersPage from "./pages/ClustersPage";
import AuthPage from "./pages/AuthPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import EmpreendimentosPage from "./pages/EmpreendimentosPage";
import QualityGatePage from "./pages/QualityGatePage";
import PlanningGatePage from "./pages/PlanningGatePage";
import OnboardingPage from "./pages/OnboardingPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import { EmpContratosListagem } from "./components/empreendimentos/EmpContratosListagem";
import NotFound from "./pages/NotFound";
import { EmpModuleWrapper } from "./components/empreendimentos/EmpModuleWrapper";
import { EmpExecucaoView } from "./components/empreendimentos/EmpExecucaoView";
import { EmpMedicoesView } from "./components/empreendimentos/EmpMedicoesView";
import { EmpIndicadoresView } from "./components/empreendimentos/EmpIndicadoresView";
import { EmpAlertasView } from "./components/empreendimentos/EmpAlertasView";
import { EmpRelatoriosView } from "./components/empreendimentos/EmpRelatoriosView";
import { EmpArquivoView } from "./components/empreendimentos/EmpArquivoView";
import { EmpAuditoriaView } from "./components/empreendimentos/EmpAuditoriaView";
import { EmpUsuariosView } from "./components/empreendimentos/EmpUsuariosView";
import { EmpConfiguracoesView } from "./components/empreendimentos/EmpConfiguracoesView";
import { EmpManualView } from "./components/empreendimentos/EmpManualView";
import PortfolioDashboardPage from "./pages/PortfolioDashboardPage";
import ExploracaoOperacionalPage from "./pages/ExploracaoOperacionalPage";
import SandboxPage from "./pages/SandboxPage";
import IntegracaoMonitorPage from "./pages/IntegracaoMonitorPage";
import BimLandingPage from "./pages/BimLandingPage";
import GestorPerformancePage from "./pages/GestorPerformancePage";
import SlaUptimePage from "./pages/SlaUptimePage";
import QADashboardPage from "./pages/QADashboardPage";
import SiafoImportPage from "./pages/SiafoImportPage";
import LandingPage from "./pages/LandingPage";
import RiskAnalyticsPage from "./pages/RiskAnalyticsPage";
import TimelineDecisoesPage from "./pages/TimelineDecisoesPage";
import RiskIntelligenceDashboard from "./pages/RiskIntelligenceDashboard";
import RiskContractAnalysis from "./pages/RiskContractAnalysis";
import RiskUODashboard from "./pages/RiskUODashboard";
import RiskTrendsDashboard from "./pages/RiskTrendsDashboard";
import RkgGovernancePage from "./pages/empreendimentos/RkgGovernancePage";
import ObservabilidadePage from "./pages/empreendimentos/ObservabilidadePage";
import FerramentasDevPage from "./pages/empreendimentos/FerramentasDevPage";
import NetunoPresentationPage from "./pages/NetunoPresentationPage";
import IFContratoDetailPage from "./pages/IFContratoDetailPage";
import IFAlertasPage from "./pages/IFAlertasPage";
import IFAdminPage from "./pages/IFAdminPage";
import IFPainelPage from "./modules/financiamento/pages/IFPainelPage";
import IFRiscoPage from "./modules/financiamento/pages/IFRiscoPage";
import IFRankingPage from "./modules/financiamento/pages/IFRankingPage";
import IFMedicoesPage from "./modules/financiamento/pages/IFMedicoesPage";
import IFResgatarConvitePage from "./modules/financiamento/pages/IFResgatarConvitePage";
import IFMeusContratosPage from "./modules/financiamento/pages/IFMeusContratosPage";
import ConvitesIFPage from "./pages/gestao/ConvitesIFPage";
import EmpLayout from "./components/empreendimentos/layout/EmpLayout";
import EmpDashboard from "./components/empreendimentos/layout/EmpDashboard";
import { getHomeRouteForRole } from "@/lib/authHomeRoute";
import { isSyncRedirectLoop } from "@/lib/redirectLoopGuard";
import { requestSpaRedirect } from "@/lib/navigationControl";
import {
  PREVIEW_TOKEN_QUERY_PARAM,
  buildPathWithPreviewToken,
  getPersistedPreviewToken,
  isLovablePreviewEnvironment,
  persistPreviewTokenFromUrl,
} from "@/lib/previewEnvironment";


// ── Throttled refetchOnWindowFocus ──────────────────────
let lastGlobalRefetch = 0;
const REFETCH_THROTTLE_MS = 30_000;

const throttledFocusRefetch = () => {
  const now = Date.now();
  if (now - lastGlobalRefetch < REFETCH_THROTTLE_MS) {
    debugLog('refetch', 'refetchOnWindowFocus suprimido por throttle', {
      elapsed: now - lastGlobalRefetch,
      throttle: REFETCH_THROTTLE_MS,
    });
    return false;
  }
  lastGlobalRefetch = now;
  debugLog('refetch', 'refetchOnWindowFocus executado');
  return true;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: throttledFocusRefetch,
      refetchOnReconnect: 'always',
      refetchOnMount: false,
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  },
});

const EmpContratoDetailPage = lazy(() => import('./pages/EmpContratoDetailPage'));

const RouteModuleFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <p className="text-sm text-muted-foreground">Carregando módulo…</p>
  </div>
);

const LazyEmpContratoDetailRoute = () => (
  <Suspense fallback={<RouteModuleFallback />}>
    <EmpContratoDetailPage />
  </Suspense>
);

// Listen for build-changed event from BuildGuard → nuke QueryClient
if (typeof window !== 'undefined') {
  window.addEventListener('netuno:build-changed', () => {
    debugLog('build', 'QueryClient.clear() executado por build-changed event');
    queryClient.clear();
  });
}

const logNav = (
  source: string,
  currentRoute: string,
  targetRoute: string,
  extra?: Record<string, unknown>
) => {
  console.log('[NetUno:NAVIGATION]', currentRoute, '→', targetRoute, {
    source,
    ...extra,
    at: new Date().toISOString(),
  });
};

// Navigate wrapper that keeps preview token on all router redirects
const Navigate = ({ to, ...props }: Omit<NavigateProps, 'to'> & { to: string }) => {
  const location = useLocation();
  const targetWithToken = buildPathWithPreviewToken(to);

  const isReplace = Boolean((props as { replace?: boolean }).replace);

  useEffect(() => {
    logNav('NavigateWrapper', `${location.pathname}${location.search}`, targetWithToken, {
      replace: isReplace,
    });
  }, [location.pathname, location.search, targetWithToken, isReplace]);

  return <RouterNavigate to={targetWithToken} {...props} />;
};

// Redirect component that preserves contractId param
const GestaoInteligenciaRedirect = () => {
  const { contractId } = useParams();
  return <Navigate to={`/empreendimentos/inteligencia/${contractId}`} replace />;
};

const RootHomeRedirect = () => {
  const { role, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (loading || !role || hasRedirected.current) return;

    if (isSyncRedirectLoop('RootHomeRedirect')) return;

    const targetPath = getHomeRouteForRole(role);
    logNav('RootHomeRedirect', `${location.pathname}${location.search}`, targetPath, { role, loading });

    if (!requestSpaRedirect('RootHomeRedirect', targetPath)) return;

    const timer = window.setTimeout(() => {
      if (hasRedirected.current) return;
      hasRedirected.current = true;
      debugLog('auth', '[NetUno:AuthFlow] Router::redirect_dashboard', { role, targetPath });
      navigate(buildPathWithPreviewToken(targetPath), { replace: true });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [role, loading, location.pathname, location.search, navigate]);

  return <RouteModuleFallback />;
};

const RootEntryRoute = () => {
  const { user, role, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  // Reset when user logs out
  useEffect(() => {
    if (!user) hasRedirected.current = false;
  }, [user]);

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (loading || !user || !role || hasRedirected.current) return;

    if (isSyncRedirectLoop('RootEntryRoute')) return;

    const targetPath = getHomeRouteForRole(role);
    logNav('RootEntryRoute', `${location.pathname}${location.search}`, targetPath, { role, loading });

    if (!requestSpaRedirect('RootEntryRoute', targetPath)) return;

    const timer = window.setTimeout(() => {
      if (hasRedirected.current) return;
      hasRedirected.current = true;
      navigate(buildPathWithPreviewToken(targetPath), { replace: true });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [user, role, loading, location.pathname, location.search, navigate]);

  if (loading) {
    return <RouteModuleFallback />;
  }

  // Unauthenticated users on /app → redirect to auth
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // User is authenticated — show fallback while redirect timer fires
  return <RouteModuleFallback />;
};

const PreviewUrlSync = () => {
  const location = useLocation();

  useEffect(() => {
    // Persist token when available in URL
    persistPreviewTokenFromUrl(location.search);

    if (!isLovablePreviewEnvironment()) return;

    const token = getPersistedPreviewToken();
    if (!token) return;

    const params = new URLSearchParams(location.search);
    if (params.get(PREVIEW_TOKEN_QUERY_PARAM) === token) return;

    params.set(PREVIEW_TOKEN_QUERY_PARAM, token);
    const nextUrl = `${location.pathname}?${params.toString()}${location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);

    debugLog('auth', '[NetUno:Preview] token_restaurado_na_url', {
      path: location.pathname,
    });
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const NavigationObserver = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousRouteRef = useRef<string | null>(null);

  useEffect(() => {
    const currentRoute = `${location.pathname}${location.search}`;
    const previousRoute = previousRouteRef.current;

    if (previousRoute) {
      logNav('NavigationObserver', previousRoute, currentRoute, {
        navigationType,
      });
    } else {
      console.log('[NetUno:NAVIGATION] INIT →', currentRoute, {
        source: 'NavigationObserver',
        navigationType,
        at: new Date().toISOString(),
      });
    }

    previousRouteRef.current = currentRoute;
  }, [location.pathname, location.search, navigationType]);

  return null;
};

/**
 * PublicRoute — Redirects authenticated users away from public pages.
 * 
 * ARCHITECTURAL RULE: This is the ONLY component that redirects
 * authenticated users away from /auth. AuthPage has ZERO navigation logic.
 * Uses a ref guard to prevent multiple <Navigate> renders during state changes.
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, role, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  // Reset redirect flag when user logs out
  useEffect(() => {
    if (!user) hasRedirected.current = false;
  }, [user]);

  // Redirect authenticated users away from public pages (e.g. /auth → dashboard)
  // Uses useEffect + setTimeout so StrictMode's double-mount cleanup prevents double navigation
  useEffect(() => {
    if (loading || !user || !role || hasRedirected.current) return;

    if (isSyncRedirectLoop('PublicRoute')) {
      debugWarn('auth', '[NetUno:AuthFlow] PublicRoute::loop_broken — rendering children');
      return;
    }

    const targetPath = getHomeRouteForRole(role);

    if (!requestSpaRedirect('PublicRoute', targetPath)) return;

    const timer = window.setTimeout(() => {
      if (hasRedirected.current) return;
      hasRedirected.current = true;
      logNav('PublicRoute', `${location.pathname}${location.search}`, targetPath, {
        role,
        loading,
      });
      debugLog('auth', '[NetUno:AuthFlow] PublicRoute::redirect_dashboard', { role, targetPath });
      navigate(buildPathWithPreviewToken(targetPath), { replace: true });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loading, user, role, location.pathname, location.search, navigate]);

  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <OfflineBanner />
        <BrowserRouter>
          <PreviewUrlSync />
          <NavigationObserver />
          <AuthProvider>
            <Routes>
              {/* ═══ LANDING & PUBLIC MARKETING ROUTES ═══ */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/landing" element={<Navigate to="/" replace />} />
              <Route path="/apresentacao" element={<NetunoPresentationPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />

              {/* ═══ APP ENTRY POINT — smart redirect to dashboard ═══ */}
              <Route path="/app" element={<RootEntryRoute />} />

              {/* ═══ PUBLIC AUTH ROUTES ═══ */}
              <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
              <Route path="/acesso-negado" element={<AccessDeniedPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/onboarding/verify" element={<OnboardingPage />} />
              <Route path="/onboarding/setup" element={<OnboardingPage />} />

              {/* ═══ AUTHENTICATED ROUTES — wrapped in AuthGate ═══ */}
              <Route path="/*" element={
                <AuthGate>
                  <AuthenticatedRoutes />
                </AuthGate>
              } />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

/**
 * All authenticated routes live here, inside AuthGate.
 * AuthGate ensures session/role/cluster hydration is complete before rendering.
 */
function AuthenticatedRoutes() {
  return (
    <Routes>
      {/* Root redirect por perfil */}
      <Route path="/" element={
        <ProtectedRoute>
          <RootHomeRedirect />
        </ProtectedRoute>
      } />

      {/* Financiamento — Monitoramento de Risco Operacional */}
      <Route path="/financiamento/painel" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Painel Fiduciário" subtitle="Financiamento de Projetos">
            <IFPainelPage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      {/* Legacy redirect: /financiamento/carteira → /financiamento/painel */}
      <Route path="/financiamento/carteira" element={<Navigate to="/financiamento/painel" replace />} />
      <Route path="/financiamento/contrato/:id" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Detalhe do Contrato" subtitle="Financiamento de Projetos" showBackButton>
            <IFContratoDetailPage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      <Route path="/financiamento/medicoes" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Medições e Execução" subtitle="Financiamento de Projetos">
            <IFMedicoesPage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      <Route path="/financiamento/ranking" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Ranking de Contratos" subtitle="Financiamento de Projetos">
            <IFRankingPage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      <Route path="/financiamento/risco" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Inteligência de Risco" subtitle="Financiamento de Projetos">
            <IFRiscoPage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      {/* Legacy redirect: /financiamento/inteligencia → /financiamento/risco */}
      <Route path="/financiamento/inteligencia" element={<Navigate to="/financiamento/risco" replace />} />
      <Route path="/financiamento/simulacoes" element={<Navigate to="/financiamento/painel" replace />} />
      <Route path="/financiamento/alertas" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Alertas Operacionais" subtitle="Financiamento de Projetos">
            <IFAlertasPage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      <Route path="/financiamento/admin" element={
        <ProtectedRoute requiredRoles={['admin', 'coadmin']}>
          <EmpLayout title="Administração de Vínculos" subtitle="Financiamento de Projetos">
            <IFAdminPage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      {/* Legacy redirect: /gestao/convites-if → /empreendimentos/convites-if */}
      <Route path="/gestao/convites-if" element={<Navigate to="/empreendimentos/convites-if" replace />} />
      <Route path="/financiamento/resgatar-convite" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Resgatar Convite" subtitle="Financiamento de Projetos">
            <IFResgatarConvitePage />
          </EmpLayout>
        </ProtectedRoute>
      } />
      <Route path="/financiamento/meus-contratos" element={
        <ProtectedRoute requiredRoles={['instituicao_financeira', 'admin', 'coadmin']}>
          <EmpLayout title="Meus Contratos" subtitle="Financiamento de Projetos">
            <IFMeusContratosPage />
          </EmpLayout>
        </ProtectedRoute>
      } />

      <Route path="/inspecoes-mobile" element={<Navigate to="/empreendimentos" replace />} />

      {/* SIAFO Empreendimentos — main application */}
      <Route path="/empreendimentos" element={
        <ProtectedRoute requiredRoles={['admin', 'coadmin', 'admin_cluster', 'gestor_planejamento', 'gestor_qualidade', 'gestor_documentacao', 'apontador', 'observador', 'gestor', 'executor', 'gestor_execucao']}>
          <EmpreendimentosPage />
        </ProtectedRoute>
      }>
        <Route path="monitoramento" element={
          <ProtectedRoute requiredRoles={['admin', 'coadmin', 'admin_cluster', 'gestor_planejamento', 'gestor_qualidade', 'gestor_documentacao', 'apontador', 'observador', 'gestor', 'executor', 'gestor_execucao']}>
            <EmpDashboard />
          </ProtectedRoute>
        } />
        <Route path="contratos" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['contratos']}>
            <EmpContratosListagem />
          </ProtectedRoute>
        } />
        <Route path="contratos/:id" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['contratos']}>
            <LazyEmpContratoDetailRoute />
          </ProtectedRoute>
        } />
        <Route path="contratos/:id/planilha" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/cronograma" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/composicoes" element={<LazyEmpContratoDetailRoute />} />
        
        <Route path="contratos/:id/frentes" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/campo" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/execucao" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/ordens_servico" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/medicoes" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/indicadores" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/workflow" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/conformidade" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/montecarlo" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/penalidades" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/glosas" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/diario" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/efetivo" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/bim" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/relatorios" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/governanca" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/arquivo" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/risk_intelligence" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/curva_s" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/reajuste" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/ledger" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/saldo" element={<LazyEmpContratoDetailRoute />} />
        <Route path="contratos/:id/excedente" element={<LazyEmpContratoDetailRoute />} />
        {/* Legacy redirect: documentos → arquivo */}
        <Route path="contratos/:id/documentos" element={<LazyEmpContratoDetailRoute />} />
        <Route path="execucao" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['execucao']}>
            <EmpModuleWrapper>{(cId, cNome, canEdit) => <EmpExecucaoView contratoId={cId} contratoNome={cNome} canEdit={canEdit} />}</EmpModuleWrapper>
          </ProtectedRoute>
        } />
        <Route path="medicoes" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['medicoes']}>
            <EmpModuleWrapper>{(cId, cNome, canEdit) => <EmpMedicoesView contratoId={cId} contratoNome={cNome} canEdit={canEdit} />}</EmpModuleWrapper>
          </ProtectedRoute>
        } />
        <Route path="indicadores" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['indicadores']}>
            <EmpModuleWrapper>{(cId, cNome, canEdit) => <EmpIndicadoresView contratoId={cId} contratoNome={cNome} canEdit={canEdit} />}</EmpModuleWrapper>
          </ProtectedRoute>
        } />
        {/* [OCULTO] Alertas — redireciona para dashboard (página preservada para reativação) */}
        <Route path="alertas" element={<Navigate to="/empreendimentos" replace />} />
        <Route path="relatorios" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['relatorios']}>
            <EmpModuleWrapper>{(cId, cNome) => <EmpRelatoriosView contratoId={cId} contratoNome={cNome} />}</EmpModuleWrapper>
          </ProtectedRoute>
        } />
        <Route path="arquivo" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['arquivo']}>
            <EmpModuleWrapper>{(cId, cNome, canEdit) => <EmpArquivoView contratoId={cId} contratoNome={cNome} canEdit={canEdit} />}</EmpModuleWrapper>
          </ProtectedRoute>
        } />
        <Route path="auditoria" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['auditoria']}>
            <EmpAuditoriaView />
          </ProtectedRoute>
        } />
        <Route path="rkg-governance" element={
          <ProtectedRoute requiredRoles={['admin', 'coadmin']}>
            <RkgGovernancePage />
          </ProtectedRoute>
        } />
        <Route path="usuarios" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['usuarios']}>
            <EmpUsuariosView />
          </ProtectedRoute>
        } />
        <Route path="configuracoes" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['configuracoes']}>
            <EmpConfiguracoesView />
          </ProtectedRoute>
        } />
        <Route path="manual" element={<EmpManualView />} />
        <Route path="portfolio" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['portfolio']}>
            <PortfolioDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="exploracao" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['exploracao']}>
            <ExploracaoOperacionalPage />
          </ProtectedRoute>
        } />
        <Route path="bim" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['bim']}>
            <BimLandingPage />
          </ProtectedRoute>
        } />
        <Route path="integracao" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['integracao']}>
            <IntegracaoMonitorPage />
          </ProtectedRoute>
        } />
        <Route path="sandbox" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['sandbox']}>
            <SandboxPage />
          </ProtectedRoute>
        } />
        <Route path="quality-gate" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['quality-gate']}>
            <QualityGatePage />
          </ProtectedRoute>
        } />
        <Route path="planning-gate" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['planning-gate']}>
            <PlanningGatePage />
          </ProtectedRoute>
        } />
        <Route path="gestor-performance" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['gestor-performance']}>
            <GestorPerformancePage />
          </ProtectedRoute>
        } />
        <Route path="sla-uptime" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['sla-uptime']}>
            <SlaUptimePage />
          </ProtectedRoute>
        } />
        <Route path="qa-dashboard" element={
          <ProtectedRoute requiredRoles={['admin', 'coadmin']}>
            <QADashboardPage />
          </ProtectedRoute>
        } />
        <Route path="siafo-import" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <SiafoImportPage />
          </ProtectedRoute>
        } />
        <Route path="mapa" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['mapa']}>
            <MapPage />
          </ProtectedRoute>
        } />
        <Route path="clusters" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['clusters']}>
            <ClustersPage />
          </ProtectedRoute>
        } />
        {/* [OCULTO] Analytics Operacional — redireciona para dashboard (página preservada para reativação) */}
        <Route path="risk-analytics" element={<Navigate to="/empreendimentos" replace />} />
        <Route path="timeline-decisoes" element={
          <ProtectedRoute requiredRoles={['admin', 'coadmin', 'admin_cluster', 'gestor_planejamento', 'gestor_qualidade', 'gestor']}>
            <TimelineDecisoesPage />
          </ProtectedRoute>
        } />
        <Route path="inteligencia" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['inteligencia']}>
            <RiskIntelligenceDashboard />
          </ProtectedRoute>
        } />
        <Route path="inteligencia/uo" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['inteligencia']}>
            <RiskUODashboard />
          </ProtectedRoute>
        } />
        <Route path="inteligencia/tendencias" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['inteligencia']}>
            <RiskTrendsDashboard />
          </ProtectedRoute>
        } />
        <Route path="inteligencia/:contractId" element={
          <ProtectedRoute requiredRoles={MODULE_VIEW_PERMISSIONS['inteligencia']}>
            <RiskContractAnalysis />
          </ProtectedRoute>
        } />
        <Route path="observabilidade" element={
          <ProtectedRoute requiredRoles={['admin', 'coadmin']}>
            <ObservabilidadePage />
          </ProtectedRoute>
        } />
        <Route path="ferramentas-dev" element={
          <ProtectedRoute requiredRoles={['admin', 'coadmin']}>
            <FerramentasDevPage />
          </ProtectedRoute>
        } />
        <Route path="convites-if" element={
          <ProtectedRoute requiredRoles={['admin', 'coadmin', 'admin_cluster']}>
            <ConvitesIFPage />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="/gestao/inteligencia" element={<Navigate to="/empreendimentos/inteligencia" replace />} />
      <Route path="/gestao/inteligencia/uo" element={<Navigate to="/empreendimentos/inteligencia/uo" replace />} />
      <Route path="/gestao/inteligencia/tendencias" element={<Navigate to="/empreendimentos/inteligencia/tendencias" replace />} />
      <Route path="/gestao/inteligencia/:contractId" element={<GestaoInteligenciaRedirect />} />
      <Route path="/risk-analytics" element={<Navigate to="/empreendimentos" replace />} />
      <Route path="/map" element={<Navigate to="/empreendimentos/mapa" replace />} />
      <Route path="/clusters" element={<Navigate to="/empreendimentos/clusters" replace />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
